import React from "react";
import { useAuth } from "@/contexts/auth";
import { Form } from "@/components/ui/form";
import { Loader2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useProfileForm } from "@/hooks/profile";
import ProfileHeader from "@/components/profile/ProfileHeader";
import PersonalInfoForm from "@/components/profile/PersonalInfoForm";
import NotificationSettings from "@/components/profile/NotificationSettings";
import PushNotificationSettings from "@/components/profile/PushNotificationSettings";
import PasswordChange from "@/components/profile/PasswordChange";
import FormActions from "@/components/profile/FormActions";
import UniversitySwapSettings from "@/components/profile/UniversitySwapSettings";
import { NotificationPermissionPrompt } from '@/components/common/NotificationPermissionPrompt';
import { useNotificationPrompt } from '@/hooks/useNotificationPrompt';


const ProfilePage: React.FC = () => {
  const { user, profile } = useAuth();
  const { form, isLoading, onSubmit } = useProfileForm();
  const { shouldShowPrompt, dismissPrompt } = useNotificationPrompt();
  
  if (!user || !profile) {
    return (
      <div className="container py-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const handleNotificationUpdate = async (settings: any) => {
    // Update the form with new notification settings
    Object.keys(settings).forEach(key => {
      form.setValue(key as any, settings[key]);
    });
    
    // Submit the form to save changes
    await onSubmit(form.getValues());
  };

  return (
    <div className="container py-8 space-y-6">
      {shouldShowPrompt && (
        <NotificationPermissionPrompt onDismiss={dismissPrompt} />
      )}
      
      <ProfileHeader />

      <div className="max-w-4xl mx-auto">
        <Tabs defaultValue="personal" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 gap-1">
            <TabsTrigger value="personal" className="text-xs md:text-sm">Personal</TabsTrigger>
            <TabsTrigger value="swaps" className="text-xs md:text-sm">Swaps</TabsTrigger>
            <TabsTrigger value="email" className="text-xs md:text-sm">Email</TabsTrigger>
            <TabsTrigger value="push" className="text-xs md:text-sm">Push</TabsTrigger>
            <TabsTrigger value="security" className="text-xs md:text-sm">Security</TabsTrigger>
          </TabsList>

          <TabsContent value="personal" className="space-y-6">
            <div className="bg-card rounded-lg border p-6 shadow-sm">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <PersonalInfoForm form={form} />
                  <FormActions isLoading={isLoading} />
                </form>
              </Form>
            </div>
          </TabsContent>

          <TabsContent value="swaps" className="space-y-6">
            <div className="bg-card rounded-lg border p-6 shadow-sm">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <UniversitySwapSettings form={form} />
                  <FormActions isLoading={isLoading} />
                </form>
              </Form>
            </div>
          </TabsContent>

          <TabsContent value="email" className="space-y-6">
            <div className="bg-card rounded-lg border p-6 shadow-sm">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <NotificationSettings form={form} />
                  <FormActions isLoading={isLoading} />
                </form>
              </Form>
            </div>
          </TabsContent>

          <TabsContent value="push" className="space-y-6">
            <PushNotificationSettings 
              pushNotifications={(profile as any)?.push_notifications}
              pushMessages={(profile as any)?.push_messages}
              pushSwapOffers={(profile as any)?.push_swap_offers}
              pushSwapResponses={(profile as any)?.push_swap_responses}
              pushWeeklyDigest={(profile as any)?.push_weekly_digest}
              pushNewReagents={(profile as any)?.push_new_reagents}
              pushReagentRequests={(profile as any)?.push_reagent_requests}
              onUpdate={handleNotificationUpdate}
            />
          </TabsContent>

          <TabsContent value="security" className="space-y-6">
            <div className="bg-card rounded-lg border p-6 shadow-sm">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <PasswordChange form={form} />
                  <FormActions isLoading={isLoading} />
                </form>
              </Form>
            </div>
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
};

export default ProfilePage;